"use client"

import React, { useState, useEffect } from "react"
import { Box, Typography, Container, Paper, Button } from "@mui/material"
import {
  Diamond,
  AutoAwesome,
  Celebration,
  PlayArrow,
  Star,
  Flare,
  Brightness7,
  Groups,
  TrendingUp,
  EmojiEvents,
} from "@mui/icons-material"
import { keyframes } from "@mui/system"
import { useParams, useNavigate } from "react-router-dom"
import { API_URL } from './config';

// אנימציות CSS מתקדמות
const sparkle = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
  50% { transform: scale(1.3) rotate(180deg); opacity: 0.7; }
`

const slideInLeft = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`

const slideInRight = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`

const slideInUp = keyframes`
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`

const slideInDown = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-15px); }
  60% { transform: translateY(-8px); }
`

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 30px rgba(138, 43, 226, 0.6), 0 0 60px rgba(138, 43, 226, 0.4);
    filter: brightness(1);
  }
  50% {
    box-shadow: 0 0 50px rgba(138, 43, 226, 0.9), 0 0 100px rgba(138, 43, 226, 0.6);
    filter: brightness(1.2);
  }
`

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-2deg); }
  50% { transform: translateY(-15px) rotate(2deg); }
`

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

// אנימציית "נפילה" מלמעלה לשורות המובילים
const dropIn = keyframes`
  0% { opacity: 0; transform: translateY(-38px) scale(0.98); }
  60% { opacity: 1; transform: translateY(6px) scale(1); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`

// זוהר סובב סביב כרטיס המנצח
const auraSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`
// ריצוד קרני אור
const rayShimmer = keyframes`
  0%, 100% { opacity: .35; }
  50% { opacity: .7; }
`
// עליית מספר הניקוד
const countPop = keyframes`
  0% { transform: scale(.6); opacity: 0; }
  70% { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`

const rainbow = keyframes`
  0% { filter: hue-rotate(0deg); }
  100% { filter: hue-rotate(360deg); }
`

// אפקט זוהר לפסים
const stripGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(255,255,255,0.3), inset 0 0 20px rgba(255,255,255,0.1);
  }
  50% {
    box-shadow: 0 0 40px rgba(255,255,255,0.5), inset 0 0 30px rgba(255,255,255,0.2);
  }
`

// אפקט בועות סבון
const bubbleFloat = keyframes`
  0%, 100% { 
    transform: translateY(0px) translateX(0px) scale(1);
    filter: blur(0px);
  }
  25% { 
    transform: translateY(-20px) translateX(10px) scale(1.02);
    filter: blur(0.5px);
  }
  50% { 
    transform: translateY(-10px) translateX(-5px) scale(0.98);
    filter: blur(0px);
  }
  75% { 
    transform: translateY(-25px) translateX(15px) scale(1.01);
    filter: blur(0.3px);
  }
`

const bubbleGlow = keyframes`
  0%, 100% {
    box-shadow: 
      0 0 40px rgba(255,255,255,0.4),
      inset 0 0 40px rgba(255,255,255,0.1),
      0 0 80px rgba(138,43,226,0.3);
  }
  50% {
    box-shadow: 
      0 0 60px rgba(255,255,255,0.6),
      inset 0 0 60px rgba(255,255,255,0.2),
      0 0 120px rgba(138,43,226,0.5);
  }
`

// אפקט מתח וציפייה רק לבועות
const bubbleTension = keyframes`
  0%, 100% { 
    transform: scale(1) rotate(0deg);
    filter: brightness(1);
  }
  10% { 
    transform: scale(1.01) rotate(0.5deg);
    filter: brightness(1.05);
  }
  20% { 
    transform: scale(0.99) rotate(-0.5deg);
    filter: brightness(0.95);
  }
  30% { 
    transform: scale(1.02) rotate(0.3deg);
    filter: brightness(1.1);
  }
  40% { 
    transform: scale(0.98) rotate(-0.3deg);
    filter: brightness(0.9);
  }
  50% { 
    transform: scale(1.01) rotate(0.2deg);
    filter: brightness(1.05);
  }
`

const suspenseShake = keyframes`
  0%, 100% { transform: translateX(0); }
  10% { transform: translateX(-2px); }
  20% { transform: translateX(2px); }
  30% { transform: translateX(-1px); }
  40% { transform: translateX(1px); }
  50% { transform: translateX(-0.5px); }
  60% { transform: translateX(0.5px); }
`

// אפקט נצנוץ מתקדם
const advancedSparkle = keyframes`
  0% { opacity: 0; transform: scale(0) rotate(0deg); }
  25% { opacity: 1; transform: scale(1.2) rotate(90deg); }
  50% { opacity: 0.8; transform: scale(0.8) rotate(180deg); }
  75% { opacity: 1; transform: scale(1.1) rotate(270deg); }
  100% { opacity: 0; transform: scale(0) rotate(360deg); }
`

// אפקט תנועת גלים ברקע
const backgroundWave = keyframes`
  0% { transform: translateX(-100%) rotate(0deg); }
  50% { transform: translateX(100%) rotate(180deg); }
  100% { transform: translateX(-100%) rotate(360deg); }
`

// אפקט מילוי פס
const fillBar = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`

// אפקט זום פועם
const zoomPulse = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.1) rotate(2deg); }
  50% { transform: scale(1.2) rotate(-2deg); }
  75% { transform: scale(1.1) rotate(1deg); }
`

// אפקט התפוצצות זיקוקים
const explode = keyframes`
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(2) rotate(180deg);
    opacity: 0.8;
  }
  100% {
    transform: scale(4) rotate(360deg);
    opacity: 0;
  }
`

// אפקט חלקיקים מתפזרים
const scatter = keyframes`
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(var(--random-x), var(--random-y)) scale(0);
    opacity: 0;
  }
`

// אפקט דופק לבועות
const heartbeat = keyframes`
  0%, 100% { 
    transform: scale(1);
    filter: brightness(1);
  }
  14% { 
    transform: scale(1.05);
    filter: brightness(1.1);
  }
  28% { 
    transform: scale(1);
    filter: brightness(1);
  }
  42% { 
    transform: scale(1.08);
    filter: brightness(1.15);
  }
  56% { 
    transform: scale(1);
    filter: brightness(1);
  }
`

// רכיב פנסים מתקדם עם צבעים משתנים
const AdvancedLightBeam = ({
  left = true,
  colors = ["rgba(138,43,226,0.3)", "rgba(0,191,255,0.3)", "rgba(50,205,50,0.3)"],
}) => {
  const [currentColor, setCurrentColor] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentColor((prev) => (prev + 1) % colors.length)
    }, 2000)
    return () => clearInterval(interval)
  }, [colors.length])

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        [left ? "left" : "right"]: 0,
        zIndex: 80,
        pointerEvents: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: 50,
          height: 30,
          background: "linear-gradient(45deg, #333, #666, #333)",
          borderRadius: "6px",
          boxShadow: "0 0 20px rgba(255,255,255,0.5)",
          mb: 1,
          animation: `${pulse} 3s infinite`,
        }}
      />
      <Box
        sx={{
          width: 0,
          height: 0,
          borderLeft: "300px solid transparent",
          borderRight: "300px solid transparent",
          borderBottom: `900px solid ${colors[currentColor]}`,
          animation: `${float} 4s ease-in-out infinite`,
          filter: "blur(15px)",
          transformOrigin: "top center",
          opacity: 0.8,
          transition: "border-bottom-color 1s ease-in-out",
        }}
      />
    </Box>
  )
}

// רכיב חלקיקים מרחפים
const FloatingParticles = ({ isTeamView = false }) => {
  const particleIcons = isTeamView
    ? [<Groups key={0} />, <Star key={1} />, <TrendingUp key={2} />, <AutoAwesome key={3} />]
    : [
        <Diamond key={0} />,
        <Flare key={1} />,
        <Brightness7 key={2} />,
        <AutoAwesome key={3} />,
        <Celebration key={4} />,
      ]

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 2,
      }}
    >
      {[...Array(isTeamView ? 25 : 30)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `${advancedSparkle} ${3 + Math.random() * 4}s infinite, ${float} ${4 + Math.random() * 3}s infinite`,
            animationDelay: `${Math.random() * 4}s, ${Math.random() * 3}s`,
          }}
        >
          {particleIcons[Math.floor(Math.random() * particleIcons.length)]}
        </Box>
      ))}
    </Box>
  )
}

// רכיב זיקוקים מתפוצצים
const ExplodingFirework = ({ delay = 0, color = "#FF69B4", x = "50%", y = "30%" }) => {
  const [show, setShow] = useState(false)
  const [exploding, setExploding] = useState(false)

  useEffect(() => {
    const showTimer = setTimeout(() => setShow(true), delay)
    const explodeTimer = setTimeout(() => setExploding(true), delay + 1000)
    return () => {
      clearTimeout(showTimer)
      clearTimeout(explodeTimer)
    }
  }, [delay])

  if (!show) return null

  return (
    <Box
      sx={{
        position: "absolute",
        top: y,
        left: x,
        zIndex: 5,
      }}
    >
      {/* זיקוק מרכזי */}
      <Box
        sx={{
          animation: exploding ? `${explode} 1s ease-out` : `${zoomPulse} 2s infinite`,
        }}
      >
        <AutoAwesome
          sx={{
            color: color,
            fontSize: 50,
            filter: `drop-shadow(0 0 20px ${color})`,
          }}
        />
      </Box>

      {/* חלקיקים מתפזרים */}
      {exploding &&
        [...Array(12)].map((_, i) => {
          const angle = i * 30 * (Math.PI / 180)
          const distance = 100 + Math.random() * 100
          const randomX = Math.cos(angle) * distance
          const randomY = Math.sin(angle) * distance

          return (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                "--random-x": `${randomX}px`,
                "--random-y": `${randomY}px`,
                animation: `${scatter} 2s ease-out`,
                animationDelay: `${i * 0.1}s`,
              }}
            >
              <AutoAwesome
                sx={{
                  color: color,
                  fontSize: 20,
                  filter: `drop-shadow(0 0 10px ${color})`,
                }}
              />
            </Box>
          )
        })}
    </Box>
  )
}

// רכיב שורת מוביל — שורה ריקה = מרווח שקוף (שומר גובה); שורה אמיתית נכנסת שלמה באנימציה
const StylishLeaderStrip = ({ leader, active, order = 0 }) => {
  const [showStrip, setShowStrip] = useState(false)

  const isEmpty = !leader || leader.empty

  useEffect(() => {
    if (!active || isEmpty) {
      setShowStrip(false)
      return
    }
    const t = setTimeout(() => setShowStrip(true), 1000 + order * 500)
    return () => clearTimeout(t)
  }, [active, order, isEmpty])

  // שורה ריקה — מרווח שקוף בלבד ששומר על גובה הטבלה (5 שורות תמיד)
  if (isEmpty) {
    return <Box sx={{ flex: 1, minHeight: 0 }} />
  }

  const position = leader?.position
  const isFirst = position === 1
  const hasMedal = position <= 3

  // צבעי מדליה/מסגרת לפי דירוג — הרקע נשאר כהה אחיד (כמו 4 ו-5)
  const perRank = {
    1: { medal: "linear-gradient(135deg,#FFEA9E,#E3A400)", medalTxt: "#5a3d00", border: "rgba(255,215,0,.5)", glow: "0 6px 22px rgba(255,193,7,.22)" },
    2: { medal: "linear-gradient(135deg,#FBFDFF,#9AA9BE)", medalTxt: "#2a3345", border: "rgba(203,213,225,.42)", glow: "0 6px 18px rgba(0,0,0,.4)" },
    3: { medal: "linear-gradient(135deg,#F5C08A,#AE6A28)", medalTxt: "#5a3410", border: "rgba(214,146,78,.42)", glow: "0 6px 18px rgba(0,0,0,.4)" },
  }
  const rs = {
    rowBg: "linear-gradient(95deg, rgba(30,48,92,.9), rgba(20,34,68,.68))",
    numColor: "#8fbaff",
    ptsColor: "#eaf1ff",
    nameColor: "#e6eefc",
    border: "rgba(110,140,210,.32)",
    glow: "0 6px 18px rgba(0,0,0,.4)",
    ...(perRank[position] || {}),
  }
  const nameStr = leader.name || leader.phone || `שחקן ${leader.id}`

  // מדליית סרט (רוזטה) לשלושת הראשונים
  const RankMedal = () => (
    <Box sx={{ position: "relative", width: "clamp(34px,4vh,46px)", height: "clamp(34px,4vh,46px)", flexShrink: 0 }}>
      <Box sx={{ position: "absolute", bottom: -7, left: "28%", width: 10, height: 18, background: rs.medal, transform: "skewX(12deg)", borderRadius: "0 0 3px 3px", opacity: 0.95, boxShadow: "0 2px 4px rgba(0,0,0,.3)" }} />
      <Box sx={{ position: "absolute", bottom: -7, right: "28%", width: 10, height: 18, background: rs.medal, transform: "skewX(-12deg)", borderRadius: "0 0 3px 3px", opacity: 0.95, boxShadow: "0 2px 4px rgba(0,0,0,.3)" }} />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: rs.medal,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 900,
          fontSize: "clamp(.9rem,1.9vh,1.2rem)",
          color: rs.medalTxt,
          border: "3px solid rgba(255,255,255,0.75)",
          boxShadow: "inset 0 2px 4px rgba(255,255,255,.5), 0 3px 10px rgba(0,0,0,.4)",
          zIndex: 1,
          ...(isFirst ? { animation: `${pulse} 2.2s infinite` } : {}),
        }}
      >
        {position}
      </Box>
    </Box>
  )

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        opacity: showStrip ? 1 : 0,
        animation: showStrip ? `${dropIn} 0.6s cubic-bezier(.3,1.25,.5,1) both` : "none",
      }}
    >
      <Box
        sx={{
          width: "100%",
          direction: "ltr",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          px: { xs: 1.5, md: 2.5 },
          borderRadius: 3,
          background: rs.rowBg,
          border: `1.5px solid ${rs.border}`,
          boxShadow: `${rs.glow}, inset 0 1px 0 rgba(255,255,255,.12)`,
          transition: "all .3s",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            width: "15%",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,.10))",
            clipPath: "polygon(35% 0, 100% 0, 100% 100%, 0% 100%)",
            pointerEvents: "none",
          },
        }}
      >
        {/* דירוג — מדליה (מספר גדול ל-4/5) */}
        <Box sx={{ width: { xs: 52, md: 68 }, display: "flex", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
          {hasMedal ? (
            <RankMedal />
          ) : (
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: "clamp(1.35rem,2.6vh,2rem)",
                lineHeight: 1,
                color: rs.numColor,
                textShadow: "0 2px 6px rgba(0,0,0,.5)",
              }}
            >
              {position}
            </Typography>
          )}
        </Box>

        {/* שם + מזהה השחקן — צמוד למדליה */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 1, md: 1.5 },
            textAlign: "left",
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "clamp(.95rem,2.1vh,1.45rem)",
              lineHeight: 1.15,
              color: rs.nameColor,
              textShadow: "1px 1px 4px rgba(0,0,0,.4)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {nameStr}
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "clamp(.66rem,1.3vh,.9rem)",
              color: "rgba(175,198,238,.72)",
              direction: "ltr",
              unicodeBidi: "plaintext",
            }}
          >
            מזהה #{leader.id}
          </Typography>
        </Box>

        {/* ניקוד */}
        <Box
          sx={{
            width: { xs: 96, md: 150 },
            textAlign: "center",
            flexShrink: 0,
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: "clamp(1.1rem,2.4vh,1.85rem)",
              color: rs.ptsColor,
              textShadow: "1px 1px 4px rgba(0,0,0,.5)",
            }}
          >
            {(leader.point || 0).toLocaleString()}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

// רכיב בועת סבון לקבוצה עם תצוגה משופרת של המוביל
const SoapBubbleTeam = ({ team, delay, position }) => {
  const [showBubble, setShowBubble] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowBubble(true), delay)
    return () => clearTimeout(timer)
  }, [delay])

  const animations = [slideInUp, slideInDown, slideInLeft, slideInRight]
  const currentAnimation = animations[position % 4]

  return (
    <Box
      sx={{
        animation: showBubble ? `${currentAnimation} 1.2s ease-out` : "none",
        opacity: showBubble ? 1 : 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      {/* שם הקבוצה מחוץ לבועה */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
          color: "white",
          textShadow: "3px 3px 8px rgba(0,0,0,0.8)",
          fontSize: "1.8rem",
          textAlign: "center",
          animation: `${pulse} 4s infinite`,
        }}
      >
        👥 {team.name}
      </Typography>

      {/* הבועה */}
      <Box
        sx={{
          width: 280,
          height: 280,
          borderRadius: "50%",
          position: "relative",
          background: `
            radial-gradient(circle at 30% 30%, 
              rgba(255,255,255,0.8), 
              rgba(255,255,255,0.4) 30%, 
              ${team.color}40 60%, 
              ${team.color}20 80%, 
              transparent 100%
            )
          `,
          border: `3px solid rgba(255,255,255,0.6)`,
          boxShadow: `
            0 20px 40px rgba(0,0,0,0.3),
            inset 0 0 60px rgba(255,255,255,0.2),
            0 0 80px ${team.color}40
          `,
          animation: `
            ${bubbleFloat} 6s ease-in-out infinite,
            ${bubbleGlow} 4s infinite,
            ${heartbeat} 8s infinite,
            ${bubbleTension} 10s infinite,
            ${suspenseShake} 12s infinite
          `,
          backdropFilter: "blur(10px)",
          "&:hover": {
            transform: "scale(1.05)",
            transition: "transform 0.3s ease",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: "15%",
            left: "20%",
            width: "25%",
            height: "25%",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.6)",
            filter: "blur(8px)",
            animation: `${sparkle} 3s infinite`,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: "60%",
            right: "25%",
            width: "15%",
            height: "15%",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.4)",
            filter: "blur(4px)",
            animation: `${sparkle} 4s infinite`,
          },
        }}
      >
        {/* תוכן הבועה - מחולק לשלושה חלקים */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            width: "85%",
            height: "85%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            py: 2,
          }}
        >
          {/* חלק עליון - נקודות הקבוצה */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography
              variant="h2"
              sx={{
                color: team.color,
                textShadow: "3px 3px 8px rgba(0,0,0,0.8)",
                fontWeight: "bold",
                fontSize: "2.8rem",
                filter: `drop-shadow(0 0 15px ${team.color})`,
                animation: `${heartbeat} 3s infinite`,
              }}
            >
              🏆 {team.totalScore}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.95)",
                textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
                fontWeight: "bold",
                fontSize: "1rem",
                mt: 0.5,
              }}
            >
              נקודות
            </Typography>
          </Box>

          {/* קו מפריד */}
          <Box
            sx={{
              width: "80%",
              height: "2px",
              background: `linear-gradient(90deg, transparent, ${team.color}80, transparent)`,
              mx: "auto",
              my: 1,
              animation: `${sparkle} 2s infinite`,
            }}
          />

          {/* חלק תחתון - מוביל הקבוצה */}
          <Box sx={{ flex: 1, flexDirection: "column", justifyContent: "center" }}>


            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.8)",
                mb: 1,
                textShadow: "1px 1px 3px rgba(0,0,0,0.6)",
                fontSize: "0.9rem",
              }}
            >
            מוביל הקבוצה
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: team.color,
                textShadow: "2px 2px 6px rgba(0,0,0,0.8)",
                fontSize: "1.5rem",
                mt: 0.5,
                filter: `drop-shadow(0 0 5px ${team.color})`,
              }}
            >
              {team.topPlayer.name || team.topPlayer.phone}
            </Typography>
        </Box>
        </Box>

        {/* חלקיקים מרחפים בתוך הבועה */}
        {[...Array(4)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: `${25 + Math.random() * 50}%`,
              left: `${25 + Math.random() * 50}%`,
              animation: `${advancedSparkle} ${4 + Math.random() * 3}s infinite`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          >
            <AutoAwesome
              sx={{
                color: team.color,
                fontSize: 14,
                opacity: 0.6,
                filter: `drop-shadow(0 0 5px ${team.color})`,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// כרטיס פודיום לקבוצה מובילה — תלת-ממד בולט
const TEAM_RANK_STYLES = {
  1: { main: "#FFD700", light: "#FFF3B0", dark: "#8a5a00", medal: "linear-gradient(135deg,#FFE98A,#E3A400)", border: "#FFD700", body: "linear-gradient(160deg, rgba(255,215,0,.22) 0%, rgba(255,193,7,.06) 45%, rgba(22,17,6,.6) 100%)", glow: "0 0 60px rgba(255,193,7,.55)", name: "linear-gradient(135deg,#FFEFA6,#FFC02E)", emblem: "linear-gradient(135deg,#FFE98A,#E3A400)" },
  2: { main: "#c8d6e8", light: "#ffffff", dark: "#5a6b82", medal: "linear-gradient(135deg,#FBFDFF,#9AA9BE)", border: "#a9bcd6", body: "linear-gradient(160deg, rgba(200,214,232,.18) 0%, rgba(200,214,232,.05) 45%, rgba(12,18,36,.6) 100%)", glow: "0 0 36px rgba(160,185,225,.4)", name: "linear-gradient(135deg,#eaf3ff,#9fc0e8)", emblem: "linear-gradient(135deg,#FBFDFF,#9AA9BE)" },
  3: { main: "#E8A15C", light: "#FFDCB0", dark: "#7a4718", medal: "linear-gradient(135deg,#F5C08A,#AE6A28)", border: "#c47f3f", body: "linear-gradient(160deg, rgba(232,161,92,.2) 0%, rgba(232,161,92,.05) 45%, rgba(20,14,8,.6) 100%)", glow: "0 0 36px rgba(210,140,70,.38)", name: "linear-gradient(135deg,#FFD9AE,#E88A32)", emblem: "linear-gradient(135deg,#F5C08A,#AE6A28)" },
  4: { main: "#7fb2ff", light: "#d6e6ff", dark: "#2a5aa8", medal: "linear-gradient(135deg,#bcd6ff,#5a8fe0)", border: "#5a8fe0", body: "linear-gradient(160deg, rgba(90,143,224,.18) 0%, rgba(90,143,224,.05) 45%, rgba(10,18,38,.6) 100%)", glow: "0 0 32px rgba(90,143,224,.38)", name: "linear-gradient(135deg,#d6e6ff,#7fb2ff)", emblem: "linear-gradient(135deg,#bcd6ff,#5a8fe0)" },
  5: { main: "#5ecfc4", light: "#c6fff8", dark: "#1a6b62", medal: "linear-gradient(135deg,#a7f0e8,#2fb0a3)", border: "#2fb0a3", body: "linear-gradient(160deg, rgba(78,205,196,.18) 0%, rgba(78,205,196,.05) 45%, rgba(8,22,26,.6) 100%)", glow: "0 0 32px rgba(78,205,196,.38)", name: "linear-gradient(135deg,#c6fff8,#5ecfc4)", emblem: "linear-gradient(135deg,#a7f0e8,#2fb0a3)" },
}

// צל מוערם ליצירת אפקט בֶּלֶט/עומק תלת-ממדי
const buildExtrude = (color, depth) =>
  Array.from({ length: depth }, (_, i) => `0 ${i + 1}px 0 ${color}`).join(",")

const TeamPodiumCard = ({ team, rank, delay = 0 }) => {
  const [show, setShow] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setShow(true), delay)
    return () => clearTimeout(t)
  }, [delay])

  const st = TEAM_RANK_STYLES[rank] || TEAM_RANK_STYLES[5]
  const isFirst = rank === 1
  const nameStr = team?.name || `קבוצה ${rank}`
  const cardW = { xs: 126, sm: 148, md: isFirst ? 206 : 170 }
  const cardH = rank === 1 ? { xs: 250, md: 376 } : rank <= 3 ? { xs: 212, md: 320 } : { xs: 182, md: 274 }
  const badge = { xs: isFirst ? 58 : 46, md: isFirst ? 82 : 64 }

  return (
    <Box sx={{ alignSelf: "flex-end", display: "flex", flexDirection: "column", alignItems: "center", animation: show ? `${fadeInUp} 0.7s cubic-bezier(.2,1,.4,1)` : "none", opacity: show ? 1 : 0, transition: "opacity .4s", zIndex: isFirst ? 3 : 2 }}>
      {/* מדליית דירוג עגולה מבריקה — צפה מעל הכרטיס */}
      <Box
        sx={{
          width: badge, height: badge, borderRadius: "50%", zIndex: 5, mb: { xs: "-22px", md: "-32px" },
          background: `radial-gradient(circle at 38% 30%, #ffffff 0%, ${st.light} 22%, ${st.main} 58%, ${st.dark} 100%)`,
          border: "3px solid rgba(255,255,255,.85)",
          boxShadow: `inset 0 -7px 13px rgba(0,0,0,.4), inset 0 7px 11px rgba(255,255,255,.65), 0 8px 20px rgba(0,0,0,.55), 0 0 28px ${st.main}bb`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 900, fontSize: { xs: isFirst ? "1.9rem" : "1.5rem", md: isFirst ? "2.7rem" : "2rem" },
          color: "#2a1c02", textShadow: "0 2px 2px rgba(255,255,255,.5), 0 -1px 1px rgba(0,0,0,.3)",
          ...(isFirst ? { animation: `${pulse} 2.4s infinite` } : {}),
        }}
      >
        {rank}
      </Box>

      {/* כרטיס זכוכית */}
      <Box
        sx={{
          position: "relative", width: cardW, height: cardH, borderRadius: 6,
          border: `2px solid ${st.border}`,
          background: `linear-gradient(160deg, ${st.main}22 0%, rgba(10,14,30,.5) 45%, ${st.main}18 100%)`,
          boxShadow: `${st.glow}, inset 0 0 44px ${st.main}1c, inset 0 1px 0 rgba(255,255,255,.28), 0 22px 50px rgba(0,0,0,.55)`,
          backdropFilter: "blur(9px)",
          display: "flex", flexDirection: "column", alignItems: "center",
          pt: { xs: `${(isFirst ? 58 : 46) * 0.62}px`, md: `${(isFirst ? 82 : 64) * 0.62}px` }, pb: { xs: 1.4, md: 2 }, px: 1.2, overflow: "hidden",
          transform: isFirst ? "scale(1.03)" : "none",
          transition: "transform .35s ease, box-shadow .35s ease",
          "&:hover": {
            transform: isFirst ? "scale(1.06)" : "scale(1.035)",
            boxShadow: `0 0 70px ${st.main}, inset 0 0 52px ${st.main}2c, inset 0 1px 0 rgba(255,255,255,.36), 0 26px 58px rgba(0,0,0,.6)`,
          },
        }}
      >
        {/* ראש קריסטל כהה (פאסטות) */}
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "44%", zIndex: 0, pointerEvents: "none", background: `linear-gradient(135deg, rgba(0,0,0,.55) 0%, transparent 42%), linear-gradient(225deg, rgba(0,0,0,.55) 0%, transparent 42%), radial-gradient(ellipse at 50% 0%, ${st.main}22, transparent 65%)` }} />

        {/* חלקיקי אור עולים בתחתית */}
        <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "50%", zIndex: 0, pointerEvents: "none", background: `repeating-linear-gradient(90deg, transparent 0px, transparent 6px, ${st.main}66 7px, transparent 8px)`, maskImage: "linear-gradient(to top, rgba(0,0,0,.9) 0%, transparent 88%)", WebkitMaskImage: "linear-gradient(to top, rgba(0,0,0,.9) 0%, transparent 88%)", opacity: 0.7 }} />

        <Box sx={{ flex: 1 }} />

        {/* שם + ניקוד */}
        <Box sx={{ position: "relative", zIndex: 1, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.35, pb: { xs: 0.5, md: 1 }, px: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: "1rem", md: isFirst ? "1.45rem" : "1.18rem" }, color: "#eef4ff", textAlign: "center", width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textShadow: "0 2px 5px rgba(0,0,0,.6)" }}>{nameStr}</Typography>
          <Box sx={{ width: "78%", height: "1px", background: `linear-gradient(90deg, transparent, ${st.main}, transparent)`, my: 0.3 }} />
          <Typography sx={{ fontWeight: 900, fontSize: { xs: "1.2rem", md: isFirst ? "1.85rem" : "1.4rem" }, color: "#fff", direction: "ltr", textShadow: `0 2px 6px rgba(0,0,0,.5), 0 0 14px ${st.main}66`, animation: show ? `${countPop} .8s ease-out` : "none" }}>{(team?.totalScore || 0).toLocaleString()}</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: ".62rem", md: ".76rem" }, letterSpacing: "1px", color: "rgba(255,255,255,.6)" }}>נקודות</Typography>
        </Box>
      </Box>

      {/* בסיס טבעת ניאון זוהר */}
      <Box sx={{ position: "relative", width: { xs: 118, md: isFirst ? 198 : 154 }, height: { xs: 30, md: isFirst ? 50 : 40 }, mt: { xs: 0.4, md: 0.8 } }}>
        <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${st.main}`, background: `radial-gradient(ellipse at 50% 45%, ${st.main}40, transparent 68%)`, boxShadow: `0 0 24px ${st.main}, inset 0 0 18px ${st.main}66` }} />
        <Box sx={{ position: "absolute", inset: "24% 20%", borderRadius: "50%", border: `2px solid ${st.light}`, boxShadow: `0 0 14px ${st.main}`, opacity: 0.85 }} />
        <Box sx={{ position: "absolute", inset: "40% 36%", borderRadius: "50%", background: `radial-gradient(ellipse, #fff, ${st.main} 70%)`, boxShadow: `0 0 18px ${st.main}` }} />
      </Box>
    </Box>
  )
}

// כפתור המשך משחק
const ContinueGameButton = () => {
  const { gameId } = useParams()
  const navigate = useNavigate()

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 30,
        left: 30,
        zIndex: 100,
      }}
    >
      <Button
        variant="contained"
        size="large"
        startIcon={<PlayArrow sx={{ fontSize: "1.6rem !important" }} />}
        onClick={() => navigate("/play/" + gameId)}
        sx={{
          background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
          color: "#fff",
          fontWeight: 800,
          fontSize: "1.15rem",
          px: 4.5,
          py: 1.6,
          borderRadius: "50px",
          letterSpacing: ".5px",
          textShadow: "0 1px 2px rgba(0,0,0,.25)",
          boxShadow: "0 10px 30px rgba(17,153,142,.55)",
          border: "2px solid rgba(255,255,255,.35)",
          animation: `${pulse} 2.5s infinite`,
          "&:hover": {
            background: "linear-gradient(135deg, #0e857b 0%, #2fd66e 100%)",
            transform: "translateY(-3px) scale(1.04)",
            boxShadow: "0 14px 38px rgba(17,153,142,.7)",
          },
          transition: "all 0.25s ease",
        }}
      >
        המשך משחק
      </Button>
    </Box>
  )
}

const h = React.createElement;

// מטא-נתונים לפי דירוג (צבע, גובה עמוד, מנצח)
const RANK_META = {
  1: { color: "#ffb900", pale: "#fff2b0", height: 475, winner: true },
  2: { color: "#1da7ff", pale: "#bdeaff", height: 415 },
  3: { color: "#ff6b3a", pale: "#ffd1b8", height: 415 },
  4: { color: "#9144ff", pale: "#e2c4ff", height: 345 },
  5: { color: "#b941ff", pale: "#ebb7ff", height: 335 },
};
// סדר תצוגה משמאל לימין
const ORDER = [5, 2, 1, 3, 4];

// נתוני דמו (עד לטעינת קבוצות)
const DEMO_COMPETITORS = [
  { rank: 5, city: "ירושלים", score: "3,120", ...RANK_META[5] },
  { rank: 2, city: "תל אביב", score: "9,230", ...RANK_META[2] },
  { rank: 1, city: "בני ברק", score: "12,450", ...RANK_META[1] },
  { rank: 3, city: "ראש העין", score: "6,890", ...RANK_META[3] },
  { rank: 4, city: "חיפה", score: "4,560", ...RANK_META[4] },
];

// בונה מתחרים מנתוני קבוצות חיים (או דמו אם אין)
function buildCompetitors(teams) {
  // teams === undefined => מסך הדגמה (/ihgfugh). מערך (גם ריק) => נתונים אמיתיים בלבד, בלי דמה.
  if (teams === undefined || teams === null) return DEMO_COMPETITORS;
  if (!teams.length) return [];
  const byRank = {};
  teams.slice(0, 5).forEach((t, i) => { byRank[i + 1] = t; });
  return ORDER.filter((r) => byRank[r]).map((r) => {
    const t = byRank[r];
    const score = t.totalScore != null ? t.totalScore : (t.total_score || 0);
    return { rank: r, city: t.name || `קבוצה ${r}`, score: Number(score).toLocaleString(), ...RANK_META[r] };
  });
}

function ActionIcon({ name }) {
  if (name === "play") {
    return h(
      "svg",
      { viewBox: "0 0 24 24", "aria-hidden": true },
      h("path", { d: "M7 4.5v15l12-7.5z", fill: "currentColor" })
    );
  }

  if (name === "players") {
    return h(
      "svg",
      { viewBox: "0 0 24 24", "aria-hidden": true },
      h("circle", { cx: "9", cy: "7.5", r: "4", fill: "currentColor" }),
      h("path", { d: "M2 20c0-4.4 2.7-7 7-7s7 2.6 7 7z", fill: "currentColor" }),
      h("circle", { cx: "17.6", cy: "8.2", r: "3", fill: "currentColor", opacity: ".75" }),
      h("path", {
        d: "M15.5 14c4.3-.2 6.5 2.1 6.5 6h-4.3c0-2.4-.7-4.3-2.2-5.8z",
        fill: "currentColor",
        opacity: ".75",
      })
    );
  }

  return h(
    "svg",
    { viewBox: "0 0 24 24", "aria-hidden": true },
    h("path", {
      d: "M19.8 10a8 8 0 1 1-2.4-4.7M20 4v6h-6",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2.4",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    })
  );
}

function Podium({ competitor, index = 0, total = 5 }) {
  const bars = Array.from({ length: 25 }, (_, i) =>
    h("i", {
      key: i,
      style: {
        height: `${18 + ((i * 31) % 93)}px`,
        animationDelay: `${-(i % 8) * 0.23}s`,
      },
    })
  );

  // כניסה — נסיעה חלקה פנימה מהצד; מקום #1 מגיע ראשון ואז לפי הדירוג
  const center = (total - 1) / 2;
  const rel = index - center;
  const enterX = Math.round(-rel * 520);
  const delay = 0.15 + (competitor.rank - 1) * 0.28;

  return h(
    "article",
    {
      className: `podium${competitor.winner ? " podium--winner" : ""}`,
      style: {
        "--glow": competitor.color,
        "--pale": competitor.pale,
        "--podium-height": `${competitor.height}px`,
        "--enter-x": `${enterX}px`,
        animation: `podiumSlide 0.85s cubic-bezier(.2,.72,.24,1) ${delay}s backwards`,
      },
      "aria-label": `מקום ${competitor.rank}, ${competitor.city}, ${competitor.score} נקודות`,
    },
    h("div", { className: "medal" }, h("span", null, competitor.rank)),
    h("div", { className: "glass-facets" }),
    h(
      "div",
      { className: "details" },
      h("h2", null, competitor.city),
      h("div", { className: "light-line" }),
      h("strong", null, competitor.score),
      h("p", null, "נקודות")
    ),
    h("div", { className: "light-bars", "aria-hidden": true }, ...bars),
    h("div", { className: "base" }, h("i"), h("i"), h("i"))
  );
}

// ===== עיצוב פודיום הקבוצות (זכוכית) — מיוצא לשימוש גם בדף המנצחים =====
export function TeamsPodiumScreen({ teams }) {
  const competitors = buildCompetitors(teams);
  return h(
    "div",
    { className: "screen podium-embed", dir: "rtl" },
    h("style", null, `
        :root {
          font-family: Arial, "Heebo", sans-serif;
          color: white;
          background: #01040d;
          font-synthesis: none;
        }

        * { box-sizing: border-box; }
        html, body, #root { width: 100%; min-width: 320px; min-height: 100%; margin: 0; }
        body { overflow-x: hidden; }
        button { font: inherit; }

        .screen {
          position: relative;
          isolation: isolate;
          min-height: 100vh;
          overflow: hidden;
          padding: 28px 24px 30px;
          background:
            radial-gradient(ellipse at 50% 91%, rgba(255, 181, 0, .22), transparent 18%),
            radial-gradient(ellipse at 29% 89%, rgba(0, 157, 255, .18), transparent 16%),
            radial-gradient(ellipse at 72% 89%, rgba(255, 83, 39, .16), transparent 16%),
            radial-gradient(ellipse at 11% 88%, rgba(157, 48, 255, .15), transparent 14%),
            radial-gradient(ellipse at 89% 88%, rgba(126, 45, 255, .15), transparent 14%),
            radial-gradient(circle at 50% 32%, rgba(0, 63, 122, .23), transparent 34%),
            radial-gradient(circle at 14% 67%, rgba(51, 16, 130, .25), transparent 31%),
            radial-gradient(circle at 88% 65%, rgba(58, 13, 117, .21), transparent 28%),
            linear-gradient(180deg, #01030a 0%, #020817 58%, #01040d 100%);
        }

        .screen::before {
          content: "";
          position: absolute;
          z-index: -2;
          inset: 145px 0 100px;
          opacity: .7;
          background-image:
            radial-gradient(circle, #ffad22 0 1.2px, transparent 2.8px),
            radial-gradient(circle, #8b43ff 0 1px, transparent 2.6px),
            radial-gradient(circle, #2788ff 0 1px, transparent 2.5px);
          background-position: 8px 2px, 54px 23px, 101px 68px;
          background-size: 119px 101px, 157px 139px, 187px 153px;
          -webkit-mask-image: linear-gradient(to bottom, transparent, #000 18%, #000 88%, transparent);
          mask-image: linear-gradient(to bottom, transparent, #000 18%, #000 88%, transparent);
          animation: stars 11s linear infinite;
        }

        .screen::after {
          content: "";
          position: absolute;
          z-index: -1;
          right: 3%;
          bottom: 72px;
          left: 3%;
          height: 155px;
          border-radius: 50%;
          background:
            radial-gradient(ellipse, rgba(36, 105, 255, .18), transparent 60%),
            repeating-radial-gradient(ellipse, transparent 0 26px, rgba(106, 59, 255, .34) 27px 28px, transparent 29px 49px);
          filter: drop-shadow(0 0 14px rgba(96, 49, 255, .44));
          transform: perspective(490px) rotateX(64deg);
        }

        @keyframes stars {
          to { background-position: 127px -99px, -103px -116px, 288px -85px; }
        }

        .heading {
          position: relative;
          z-index: 5;
          text-align: center;
        }

        .trophy {
          display: block;
          font-size: clamp(45px, 4vw, 64px);
          line-height: 1;
          filter: drop-shadow(0 0 11px rgba(255, 185, 0, .55));
        }

        .heading h1 {
          margin: 9px 0 0;
          color: #f5f6f8;
          font-size: clamp(44px, 5.15vw, 78px);
          font-weight: 900;
          line-height: 1.05;
          letter-spacing: 8px;
          text-shadow: 0 3px 0 #a7afb9, 0 8px 24px #000;
        }

        .subtitle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 24px;
          margin-top: 18px;
          color: #ffc426;
          font-size: clamp(22px, 2.25vw, 34px);
          font-weight: 800;
          letter-spacing: 4.5px;
        }

        .subtitle::before, .subtitle::after {
          content: "";
          width: clamp(82px, 10vw, 145px);
          height: 2px;
          background: linear-gradient(90deg, transparent, #ffc400);
          box-shadow: 0 0 6px #f6a900;
        }

        .subtitle::after { transform: scaleX(-1); }

        .podiums {
          direction: ltr;
          position: relative;
          z-index: 2;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          gap: clamp(20px, 3.65vw, 56px);
          width: min(1420px, 96vw);
          min-height: 610px;
          margin: -1px auto 0;
          padding: 65px 0 43px;
        }

        .podium {
          direction: rtl;
          position: relative;
          isolation: isolate;
          width: clamp(150px, 14vw, 214px);
          height: calc(var(--podium-height) - 35px);
          flex: 0 0 clamp(150px, 14vw, 214px);
          border: 2px solid var(--glow);
          border-bottom: 0;
          border-radius: 30px 30px 20px 20px;
          background:
            linear-gradient(132deg, color-mix(in srgb, var(--glow) 23%, transparent), transparent 34%),
            linear-gradient(225deg, rgba(255,255,255,.11), transparent 32%),
            linear-gradient(180deg, rgba(8,21,41,.67), rgba(1,5,14,.48));
          -webkit-backdrop-filter: blur(7px) saturate(140%);
          backdrop-filter: blur(7px) saturate(140%);
          box-shadow:
            inset 0 0 0 3px rgba(255,255,255,.14),
            inset 9px 0 18px color-mix(in srgb, var(--glow) 15%, transparent),
            inset -8px 0 18px rgba(255,255,255,.05),
            inset 0 0 47px color-mix(in srgb, var(--glow) 17%, transparent),
            0 0 7px white,
            0 0 17px var(--glow),
            0 0 39px color-mix(in srgb, var(--glow) 68%, transparent);
        }

        .podium:first-child,
        .podium:last-child {
          width: clamp(140px, 12.2vw, 186px);
          flex-basis: clamp(140px, 12.2vw, 186px);
        }

        .podium--winner {
          z-index: 5;
          width: clamp(192px, 16.7vw, 256px);
          flex-basis: clamp(192px, 16.7vw, 256px);
          border-width: 3px;
          border-bottom-width: 0;
          box-shadow:
            inset 0 0 0 3px rgba(255,255,255,.16),
            inset 10px 0 22px rgba(255,184,0,.12),
            inset 0 0 64px rgba(255,184,0,.16),
            0 0 8px #fff2a4,
            0 0 21px var(--glow),
            0 0 52px rgba(255,174,0,.5);
        }

        .glass-facets {
          position: absolute;
          inset: 0;
          overflow: hidden;
          border-radius: inherit;
          opacity: .69;
          background:
            linear-gradient(132deg, transparent 14%, rgba(255,255,255,.28) 14.5% 15%, transparent 15.5% 35%, rgba(255,255,255,.13) 35.5% 36%, transparent 36.5%),
            linear-gradient(48deg, transparent 27%, color-mix(in srgb, var(--glow) 21%, transparent) 27.5% 39%, transparent 39.5%),
            conic-gradient(from 36deg at 31% 14%, transparent, color-mix(in srgb, var(--glow) 37%, transparent), transparent 31%),
            conic-gradient(from 210deg at 78% 14%, transparent, rgba(255,255,255,.22), transparent 30%);
          clip-path: polygon(0 0,100% 0,100% 58%,87% 46%,70% 52%,53% 36%,34% 49%,17% 34%,0 54%);
        }

        .podium::before {
          content: "";
          position: absolute;
          z-index: 2;
          inset: 8px 8px 4px;
          pointer-events: none;
          border-top: 1px solid rgba(255,255,255,.4);
          border-right: 1px solid rgba(255,255,255,.09);
          border-left: 1px solid rgba(255,255,255,.25);
          border-radius: 22px 22px 13px 13px;
          background:
            linear-gradient(105deg, transparent 8%, rgba(255,255,255,.055) 15%, transparent 22%),
            linear-gradient(74deg, transparent 67%, color-mix(in srgb, var(--glow) 8%, transparent) 75%, transparent 84%);
          box-shadow: inset 0 15px 22px rgba(255,255,255,.025);
        }

        .podium::after {
          display: none;
        }

        .medal {
          position: absolute;
          z-index: 6;
          top: -62px;
          left: 50%;
          display: grid;
          width: 101px;
          height: 101px;
          place-items: center;
          border: 2px solid color-mix(in srgb, var(--glow) 82%, var(--pale));
          outline: 3px solid color-mix(in srgb, var(--glow) 72%, #080d18);
          outline-offset: 1px;
          border-radius: 50%;
          color: var(--pale);
          background:
            radial-gradient(
              circle at 35% 28%,
              var(--pale) 0%,
              var(--glow) 27%,
              color-mix(in srgb, var(--glow) 46%, #030814) 57%,
              #030712 79%
            );
          box-shadow:
            inset 0 0 0 3px rgba(255,255,255,.3),
            inset 0 0 0 7px color-mix(in srgb, var(--glow) 72%, #08101c),
            inset 0 0 20px #000,
            0 3px 0 #08101b,
            0 0 0 2px #050a13,
            0 0 5px var(--glow),
            0 0 17px var(--glow);
          transform: translateX(-50%);
        }

        .podium--winner .medal {
          top: -69px;
          width: 116px;
          height: 116px;
        }

        .medal::after {
          content: "";
          position: absolute;
          inset: 6px;
          z-index: 1;
          border: 1px solid color-mix(in srgb, var(--glow) 74%, var(--pale));
          border-radius: 50%;
          box-shadow:
            inset 0 0 8px rgba(255,255,255,.18),
            0 0 5px color-mix(in srgb, var(--glow) 65%, transparent);
        }

        .medal::before {
          content: "";
          position: absolute;
          z-index: 1;
          top: 9px;
          right: 17px;
          left: 17px;
          height: 32%;
          border-radius: 50%;
          background: linear-gradient(180deg, rgba(255,255,255,.68), rgba(255,255,255,.05));
          opacity: .55;
          transform: rotate(-7deg);
        }

        .medal span {
          position: relative;
          z-index: 2;
          font-size: 57px;
          font-weight: 900;
          line-height: 1;
          text-shadow: 0 4px 2px rgba(0,0,0,.75);
        }

        .podium--winner .medal span { font-size: 64px; }

        .details {
          position: relative;
          z-index: 3;
          margin-top: 43%;
          padding: 0 18px;
          text-align: center;
        }

        .podium:nth-child(2) .details,
        .podium:nth-child(4) .details {
          margin-top: 49.5%;
        }

        .podium--winner .details { margin-top: 52%; }

        .podium:first-child .details,
        .podium:last-child .details {
          margin-top: 46%;
        }

        .details h2 {
          margin: 0;
          color: var(--pale);
          font-size: clamp(22px, 2vw, 33px);
          font-weight: 800;
          white-space: nowrap;
        }

        .light-line {
          width: 78%;
          height: 1px;
          margin: 24px auto;
          background: linear-gradient(90deg, transparent, var(--glow), white, var(--glow), transparent);
          box-shadow: 0 0 8px var(--glow);
        }

        .details strong {
          display: block;
          color: var(--pale);
          font-size: clamp(25px, 2.15vw, 36px);
          font-weight: 800;
          line-height: 1.1;
          letter-spacing: 1px;
        }

        .podium:first-child .light-line,
        .podium:last-child .light-line {
          margin-block: 18px;
        }

        .podium:first-child .details strong,
        .podium:last-child .details strong {
          font-size: 30px;
        }

        .details p {
          margin: 8px 0 0;
          color: var(--glow);
          font-size: clamp(17px, 1.45vw, 23px);
          font-weight: 700;
        }

        .podium--winner .details h2,
        .podium--winner .details strong { color: #fffbed; }

        .podium--winner .details strong { font-size: clamp(31px, 2.6vw, 40px); }

        .podium--winner .light-line { margin-block: 30px; }

        .light-bars {
          position: absolute;
          right: 8px;
          bottom: 4px;
          left: 8px;
          display: flex;
          height: 135px;
          align-items: flex-end;
          justify-content: space-around;
          overflow: hidden;
          opacity: .92;
          -webkit-mask-image: linear-gradient(to bottom, transparent, #000 36%);
          mask-image: linear-gradient(to bottom, transparent, #000 36%);
        }

        .light-bars i {
          display: block;
          width: 1px;
          max-height: 112px;
          background: linear-gradient(to top, #fff, var(--glow) 55%, transparent);
          box-shadow: 0 0 5px var(--glow);
          transform-origin: bottom;
          animation: bars 2.6s ease-in-out infinite alternate;
        }

        @keyframes bars { to { transform: scaleY(.45); opacity: .35; } }

        .base {
          position: absolute;
          z-index: -1;
          right: -18px;
          bottom: -52px;
          left: -18px;
          height: 52px;
          overflow: visible;
          border: 2px solid color-mix(in srgb, var(--glow) 76%, #10131a);
          border-radius: 50% / 23%;
          background:
            linear-gradient(
              90deg,
              rgba(0,0,0,.72) 0%,
              rgba(0,0,0,.2) 18%,
              rgba(255,255,255,.12) 48%,
              rgba(0,0,0,.18) 72%,
              rgba(0,0,0,.76) 100%
            ),
            linear-gradient(
              180deg,
              color-mix(in srgb, var(--glow) 88%, var(--pale)) 0%,
              var(--glow) 14%,
              color-mix(in srgb, var(--glow) 76%, #080b12) 35%,
              color-mix(in srgb, var(--glow) 61%, #03060c) 70%,
              color-mix(in srgb, var(--glow) 42%, #010309) 100%
            );
          box-shadow:
            inset 0 5px 4px rgba(255,255,255,.22),
            inset 0 -8px 12px rgba(0,0,0,.72),
            0 5px 0 color-mix(in srgb, var(--glow) 35%, #010207),
            0 10px 12px #000,
            0 0 17px color-mix(in srgb, var(--glow) 65%, transparent),
            0 24px 32px color-mix(in srgb, var(--glow) 28%, transparent);
          transform: translateY(-5px);
        }

        .base::before, .base::after, .base i {
          content: "";
          position: absolute;
          display: block;
          left: 50%;
          pointer-events: none;
          border-radius: 50% / 38%;
          transform: translateX(-50%);
        }

        .base::before {
          z-index: 3;
          top: -2px;
          display: block;
          width: calc(100% - 4px);
          height: 38%;
          border: 3px solid color-mix(in srgb, var(--glow) 58%, var(--pale));
          background: radial-gradient(
            ellipse at 50% 42%,
            color-mix(in srgb, var(--glow) 58%, #171b25) 0%,
            color-mix(in srgb, var(--glow) 45%, #070a11) 64%,
            color-mix(in srgb, var(--glow) 32%, #010207) 100%
          );
          box-shadow:
            inset 0 3px 4px rgba(255,255,255,.22),
            inset 0 -5px 8px rgba(0,0,0,.52),
            0 0 6px color-mix(in srgb, var(--glow) 62%, transparent);
        }

        .base i:first-child {
          z-index: 5;
          top: -11px;
          width: 88%;
          height: 36%;
          border-width: 3px;
          border-style: solid;
          border-color: color-mix(in srgb, var(--glow) 60%, var(--pale));
          background: radial-gradient(
            ellipse at 50% 45%,
            color-mix(in srgb, var(--glow) 70%, var(--pale)) 0%,
            color-mix(in srgb, var(--glow) 62%, #11151d) 55%,
            color-mix(in srgb, var(--glow) 48%, #03050b) 100%
          );
          box-shadow:
            inset 0 4px 5px rgba(255,255,255,.24),
            inset 0 -7px 10px rgba(0,0,0,.46),
            0 1px 0 color-mix(in srgb, var(--pale) 75%, var(--glow)),
            0 0 7px color-mix(in srgb, var(--glow) 72%, transparent);
        }

        .base::after,
        .base i:nth-child(2),
        .base i:nth-child(3) {
          display: none;
        }

        .podium--winner .base {
          right: -25px;
          bottom: -80px;
          left: -25px;
          height: 80px;
        }

        .podium:nth-child(2) .base,
        .podium:nth-child(4) .base {
          right: -21px;
          bottom: -64px;
          left: -21px;
          height: 64px;
        }

        @media (min-width: 1001px) {
          .podium--winner {
            transform: translateY(10px) scale(1.04);
            transform-origin: bottom center;
          }

          .podium:nth-child(2),
          .podium:nth-child(4) {
            z-index: 2;
            transform: scale(.97);
            transform-origin: bottom center;
          }

          .podium:first-child,
          .podium:last-child {
            z-index: 1;
            transform: translateY(-3px) scale(.94);
            transform-origin: bottom center;
          }
        }

        .actions {
          direction: ltr;
          position: relative;
          z-index: 10;
          display: flex;
          justify-content: center;
          gap: 24px;
          margin: 82px auto 0;
        }

        .action {
          direction: rtl;
          display: flex;
          width: min(240px, 29vw);
          height: 67px;
          align-items: center;
          justify-content: center;
          gap: 13px;
          border: 1px solid #7282ab;
          border-radius: 999px;
          color: #f4f6fb;
          background: linear-gradient(180deg, rgba(12,25,52,.86), rgba(3,8,21,.94));
          box-shadow: inset 0 0 17px rgba(27,56,110,.25), 0 5px 20px rgba(0,0,0,.35);
          font-size: clamp(16px, 1.5vw, 21px);
          font-weight: 700;
          cursor: pointer;
          transition: .2s ease;
        }

        .action:hover {
          border-color: #c0d2ff;
          box-shadow: 0 0 19px rgba(64,112,255,.3);
          transform: translateY(-2px);
        }

        .action svg { width: 25px; height: 25px; color: #e8efff; }
        .action--players svg { color: #6742ff; filter: drop-shadow(0 0 5px #4828ff); }

        @media (max-width: 1000px) {
          .podiums { gap: 10px; width: 100%; }
          .podium { min-width: 135px; }
          .medal { top: -49px; width: 82px; height: 82px; }
          .podium--winner .medal { top: -57px; width: 96px; height: 96px; }
          .medal span, .podium--winner .medal span { font-size: 47px; }
        }

        @media (max-width: 760px) {
          .screen { padding-inline: 10px; }
          .heading h1 { letter-spacing: 3px; }
          .podiums {
            justify-content: flex-start;
            width: max-content;
            min-width: 100%;
            margin-top: 4px;
            padding-inline: 24px;
            transform: scale(.71);
            transform-origin: bottom left;
          }
          .podium,
          .podium:first-child,
          .podium:last-child {
            width: 174px;
            min-width: 174px;
            flex-basis: 174px;
          }
          .podium--winner {
            width: 208px;
            min-width: 208px;
            flex-basis: 208px;
          }
          .actions { gap: 7px; margin-top: -88px; }
          .action { width: 31.5vw; height: 56px; gap: 6px; padding: 0 7px; font-size: 13px; }
          .action svg { width: 18px; height: 18px; }
        }

        @media (max-width: 480px) {
          .screen { padding-top: 19px; }
          .trophy { font-size: 39px; }
          .heading h1 { font-size: 34px; }
          .subtitle { gap: 10px; font-size: 20px; }
          .subtitle::before, .subtitle::after { width: 56px; }
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; }
        }
      

/* ===== התאמות שילוב + כניסה מחליקה ===== */
.podium-embed{min-height:auto !important;background:none !important;padding:14px 0 40px !important;overflow:visible !important;}
.podium-embed .podiums{zoom:.82;min-height:auto;padding:44px 0 20px;}
/* הורדת הרצפה לתחתית מתחת לקבוצות (במקום לרחף באוויר) */
.podium-embed::after{bottom:-14px !important;height:120px !important;}
.podium-embed::before{inset:30px 0 34px !important;}
@keyframes podiumSlide{0%{opacity:0;transform:translateX(var(--enter-x,0)) translateY(-20px);}100%{opacity:1;transform:translateX(0) translateY(0);}}
`),
    h(
      "section",
      { className: "podiums", "aria-label": "דירוג הקבוצות" },
      ...competitors.map((competitor, index) =>
        h(Podium, { key: competitor.rank, competitor, index, total: competitors.length })
      )
    )
  );
}


export default function LeadersPage() {
  const [showCelebration, setShowCelebration] = useState(false)
  const [showTitle, setShowTitle] = useState(false)
  const [showTeams, setShowTeams] = useState(false)
  const [showTable, setShowTable] = useState(false)
  const [leaders, setLeaders] = useState([])
  const [teams, setTeams] = useState([])
  const [replayKey, setReplayKey] = useState(0)
  const { gameId } = useParams()
  const navigate = useNavigate()

  // "חזור שוב" — הרצת אנימציית החשיפה מחדש
  const replay = () => setReplayKey((k) => k + 1)

  // עצירת מוזיקת המובילים לפני מעבר למשחק
  const stopMusicAndPlay = () => {
    try {
      if (window.currentAudio) { window.currentAudio.pause(); window.currentAudio.currentTime = 0 }
    } catch (e) {}
    navigate("/play/" + gameId)
  }

  // 3 כפתורי פעולה צפים בתחתית — מסגרת עדינה מסביב, כולם באותו צבע, אייקונים שונים
  const actionButtons = (isTeams) => {
    const btn = {
      px: { xs: 2, md: 2.8 },
      py: { xs: 0.9, md: 1.1 },
      borderRadius: 999,
      fontWeight: 800,
      fontSize: { xs: "0.85rem", md: "1.02rem" },
      textTransform: "none",
      color: "#dbe6ff",
      border: "1.5px solid rgba(150,175,225,.4)",
      background: "linear-gradient(135deg, rgba(40,58,102,.55), rgba(20,32,64,.65))",
      backdropFilter: "blur(8px)",
      whiteSpace: "nowrap",
      transition: "all .25s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        background: "linear-gradient(135deg, rgba(50,72,125,.75), rgba(28,44,84,.85))",
        borderColor: "rgba(150,175,225,.7)",
      },
    }
    return (
      <Box
        sx={{
          position: "fixed",
          left: "50%",
          bottom: { xs: 14, md: 22 },
          transform: "translateX(-50%)",
          zIndex: 100,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 1, md: 1.5 },
          p: { xs: 0.8, md: 1 },
          borderRadius: 999,
          border: "1px solid rgba(120,150,220,.3)",
          background: "rgba(8,14,32,.55)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 10px 30px rgba(0,0,0,.45)",
        }}
      >
        {/* חזור שוב */}
        <Button onClick={replay} sx={btn}>
          ↻ חזור שוב
        </Button>

        {/* מעבר לקבוצות / חזרה לשחקנים — מוצג רק אם יש קבוצות */}
        {(isTeams || teams.length > 0) && (
          <Button onClick={() => setShowTeams(!isTeams)} sx={btn}>
            {isTeams ? "👤 חזרה לשחקנים" : "👥 מעבר לקבוצות"}
          </Button>
        )}

        {/* המשך משחק */}
        <Button onClick={stopMusicAndPlay} sx={btn}>
          ▶ המשך משחק
        </Button>
      </Box>
    )
  }

  // צבע אחיד למובילים
  const leaderColor = "#4ECDC4"

  // צבעים לקבוצות
  const teamColors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#a78bfa"]


  useEffect(() => {
    const titleTimer = setTimeout(() => setShowTitle(true), 500)
    const celebrationTimer = setTimeout(() => setShowCelebration(true), 1200)
    const tableTimer = setTimeout(() => setShowTable(true), 1800) // הטבלה נכנסת רק אחרי שהכותרת נכנסה
    const teamsTimer = setTimeout(() => setShowTeams(true), 20000)

    // טען קבוצות
    fetch(`${API_URL}/get_teams/${gameId}`)
      .then((res) => res.json())
      .then((data) => {
        const formattedTeams = data.slice(0, 5).map((item, index) => ({
          id: item.id,
          name: item.name || `קבוצה ${index + 1}`,
          color: teamColors[index],
          totalScore: item.total_score || 0,
          topPlayer: {
            id: item.top_player?.id,
            name: item.top_player?.name || item.top_player?.phone,
            phone: item.top_player?.phone,
            score: item.top_player?.point || 0,
          },
        }))
        setTeams(formattedTeams)
      })
      .catch(() => {
      })

    // טען מובילים (בלי קשר לקבוצות)
    fetch(`${API_URL}/get_leaders/${gameId}`)
      .then((res) => res.json())
      .then((data) => {
        const formattedLeaders = data.slice(0, 5).map((item, index) => ({
          id: item.id,
          name: item.name || item.phone || `שחקן ${item.id}`,
          point: item.point || 0,
          phone: item.phone,
          position: index + 1,
          color: leaderColor,
        }))
        setLeaders(formattedLeaders)
      })
      .catch(() => {
      })

    return () => {
      clearTimeout(titleTimer)
      clearTimeout(celebrationTimer)
      clearTimeout(tableTimer)
      clearTimeout(teamsTimer)
    }
  }, [])

  // מעבר אוטומטי למשחק אחרי 20 שניות מהצגת הקבוצות — עם עצירת מוזיקה
  useEffect(() => {
    if (showTeams) {
      const gameTimer = setTimeout(() => {
        try {
          if (window.currentAudio) { window.currentAudio.pause(); window.currentAudio.currentTime = 0 }
        } catch (e) {}
        navigate("/play/" + gameId)
      }, 20000)

      return () => clearTimeout(gameTimer)
    }
  }, [showTeams, navigate, gameId])

  // השמעת מוזיקה מיד עם טעינת הדף (לפני האנימציות)
  useEffect(() => {
    if (window.currentAudio) {
      try {
        window.currentAudio.pause()
        window.currentAudio.currentTime = 0
      } catch (e) {}
    }
    const audio = new Audio("/music/leaders.mp3")
    audio.volume = 0.7
    audio.play().catch(console.error)
    window.currentAudio = audio
  }, [])

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#050a18",
        background: `
          radial-gradient(ellipse 90% 55% at 50% -12%, rgba(30,58,120,0.55) 0%, transparent 60%),
          radial-gradient(ellipse 130% 100% at 50% 0%, #0e1b3c 0%, #0a1430 38%, #070e22 68%, #050a18 100%)
        `,
        position: "relative",
        overflow: "hidden",
        py: { xs: 2, md: 2.5 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* פסי אור אלכסוניים בפינות + זוהר תחתון */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          background: `
            linear-gradient(115deg, transparent 0%, rgba(90,160,255,0.08) 6%, transparent 12%),
            linear-gradient(65deg, transparent 0%, rgba(90,160,255,0.08) 6%, transparent 12%),
            radial-gradient(circle at 50% -8%, rgba(60,120,240,0.18), transparent 42%),
            radial-gradient(ellipse 55% 6% at 50% 100%, rgba(60,130,240,0.22), transparent 70%)
          `,
        }}
      />

      <Container maxWidth={showTeams && teams.length > 0 ? "xl" : "lg"} sx={{ position: "relative", zIndex: 10, pb: { xs: 12, md: 11 }, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {showTeams && teams.length > 0 ? (
          // דף הקבוצות — רק אם יש קבוצות אמיתיות
          <>
            {/* כותרת קבוצות — נכנסת באנימציה על הופעת המסך (כמו בשחקנים) */}
            <Box
              sx={{
                textAlign: "center",
                mb: { xs: 2, md: 2.5 },
                mt: 0.5,
                flexShrink: 0,
                animation: `${fadeInUp} 1.6s ease-out both`,
              }}
            >
              <Box sx={{ fontSize: { xs: "2.4rem", md: "3.2rem" }, lineHeight: 1, mb: 0.3, filter: "drop-shadow(0 6px 14px rgba(255,180,0,.5))" }}>🏆</Box>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "2.2rem", md: "3.4rem" },
                  letterSpacing: "1px",
                  background: "linear-gradient(180deg, #ffffff 0%, #dbe6ff 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  filter: "drop-shadow(0 4px 10px rgba(0,0,0,.6))",
                }}
              >
                דירוג הקבוצות
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mt: 1 }}>
                <Box sx={{ height: 2, width: { xs: 40, md: 90 }, background: "linear-gradient(90deg, transparent, #FFC107)" }} />
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.2rem", md: "1.6rem" },
                    letterSpacing: "2px",
                    background: "linear-gradient(135deg, #FFE55C, #FFB300)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  קליקרים
                </Typography>
                <Box sx={{ height: 2, width: { xs: 40, md: 90 }, background: "linear-gradient(90deg, #FFC107, transparent)" }} />
              </Box>
            </Box>

            {/* פודיום הקבוצות — עיצוב הזכוכית מ-ihgfugh עם נתונים חיים, ממורכז אנכית */}
            <Box
              key={`tp-${replayKey}`}
              sx={{ width: "100%", flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <TeamsPodiumScreen teams={teams} />
            </Box>

            {actionButtons(true)}

          </>
        ) : (
          // דף המובילים בלבד
          <>
            {/* כותרת מתקדמת — גביע + כיתוב מותג */}
            <Box
              sx={{
                textAlign: "center",
                mb: { xs: 2, md: 2.5 },
                mt: 0.5,
                animation: showTitle ? `${fadeInUp} 2s ease-out` : "none",
                opacity: showTitle ? 1 : 0,
              }}
            >
              <Box sx={{ fontSize: { xs: "2.4rem", md: "3.2rem" }, lineHeight: 1, mb: 0.3, filter: "drop-shadow(0 6px 14px rgba(255,180,0,.5))" }}>🏆</Box>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "2.2rem", md: "3.4rem" },
                  letterSpacing: "1px",
                  background: "linear-gradient(180deg, #ffffff 0%, #dbe6ff 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  filter: "drop-shadow(0 4px 10px rgba(0,0,0,.6))",
                }}
              >
                השחקנים המובילים
              </Typography>
              {/* תת-כותרת מותג עם קווי זהב */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mt: 1 }}>
                <Box sx={{ height: 2, width: { xs: 40, md: 90 }, background: "linear-gradient(90deg, transparent, #FFC107)" }} />
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.2rem", md: "1.6rem" },
                    letterSpacing: "2px",
                    background: "linear-gradient(135deg, #FFE55C, #FFB300)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  קליקרים
                </Typography>
                <Box sx={{ height: 2, width: { xs: 40, md: 90 }, background: "linear-gradient(90deg, #FFC107, transparent)" }} />
              </Box>
            </Box>

            {/* טבלת המובילים — מסגרת חיצונית, מתמלאת לגובה הדף, נכנסת אחרי הכותרת */}
            <Box
              key={`lt-${replayKey}`}
              sx={{
                width: "100%",
                maxWidth: "1000px",
                mx: "auto",
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                p: { xs: 1.2, md: 1.8 },
                borderRadius: 4,
                border: "1px solid rgba(120,150,220,.28)",
                background: "linear-gradient(180deg, rgba(20,34,68,.45), rgba(10,20,44,.25))",
                boxShadow: "0 20px 60px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.06)",
                position: "relative",
                opacity: showTable ? 1 : 0,
                transform: showTable ? "translateY(0)" : "translateY(20px)",
                transition: "opacity .6s ease, transform .6s ease",
              }}
            >
              {/* כותרות עמודות */}
              <Box
                sx={{
                  direction: "ltr",
                  display: "flex",
                  alignItems: "center",
                  px: { xs: 2.5, md: 4 },
                  pb: 1.2,
                  color: "#aebfe0",
                  fontWeight: 700,
                  fontSize: { xs: "0.9rem", md: "1.15rem" },
                  borderBottom: "1px solid rgba(120,150,220,.25)",
                  mb: 1.2,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ width: { xs: 52, md: 68 }, textAlign: "center", flexShrink: 0 }}>דירוג</Box>
                <Box sx={{ flex: 1, textAlign: "left", px: { xs: 1, md: 1.5 } }}>שחקן</Box>
                <Box sx={{ width: { xs: 96, md: 150 }, textAlign: "center", flexShrink: 0 }}>נקודות</Box>
              </Box>

              {/* שורות — תמיד 5 (ריקות אם אין שחקן), ממלאות את הגובה */}
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: { xs: 0.7, md: 1 } }}>
                {Array.from({ length: 5 }, (_, i) => leaders[i] || { position: i + 1, empty: true }).map((slot, index) => (
                  <StylishLeaderStrip
                    key={slot.position}
                    leader={slot}
                    active={showTable}
                    order={index}
                  />
                ))}
              </Box>
            </Box>

            {actionButtons(false)}
          </>
        )}
      </Container>
    </Box>
  )
}
